import { CUSTOM_ELEMENTS_SCHEMA, Component, EventEmitter, HostListener, Input, OnInit, Output, Renderer2, inject } from "@angular/core";
import { AuthorizationService } from "src/app/shared/services/authorization.service";
import { Market } from "src/app/shared/models/market.model";
import { MarketSelectionService } from "src/app/shared/services/market-selection.service";
import { MenuStateService } from "src/app/shared/services/menu.service";
import { Router } from "@angular/router";
import { AppReadyService } from "src/app/shared/services/app-ready.service";
import { MarketService } from "src/app/shared/services/market.service";
import { first, Observable } from "rxjs";
import { TegelModule } from "@scania/tegel-angular-17";
import { AsyncPipe, CommonModule } from "@angular/common";

@Component({
    selector: "app-sidebar",
    templateUrl: "./sidebar.component.html",
    styleUrls: ["./sidebar.component.scss"],
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [CommonModule, TegelModule, AsyncPipe]
})
export class SidebarComponent implements OnInit {
  private renderer = inject(Renderer2);
  private appReadyService = inject(AppReadyService);
  private authService = inject(AuthorizationService);
  private menuStateService = inject(MenuStateService);
  private router = inject(Router);
  private marketService = inject(MarketService);
  private mktSelectService = inject(MarketSelectionService);

  collapsed = true;
  public roles!: string[];
  isMenuOpened = false;
  isMobile = window.innerWidth < 768;
  userName = '';
  userEmail = '';
  userRoles: string[] = [];
  private userMarketRoles: string[] = [];
  selectedMarketLabel = "MARKETS";
  public userMarkets: Market[] = [];
  public selectedMarket: Market = new Market(0, "MARKETS", "", false);

  @Input() mobileMenuOpen = false;
  menuItems = [
    {
      label: "Home",
      icon: 'home',
      path: '/home',
      subMenu: null,
      allowedRoles: ["NO_ROLE", "CPM_USER", "CPM_MASTER", "RTM_USER", "RTM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
    },
    {
      label: "Real Time Management",
      icon: 'timer',
      path: '',
      allowedRoles: ["RTM_USER", "RTM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
      subMenu: [
        {
          label: "Register",
          icon: 'document_plus', 
          path: "/real-time-management/create",
          allowedRoles: ["RTM_USER", "RTM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
        },
        {
          label: "Status/Update",
          icon: 'search', 
          path: "/real-time-management/search",
          allowedRoles: ["RTM_USER", "RTM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
        },
        {
          label: "Setup",
          icon: 'tool', 
          path: "/real-time-management/setup",
          allowedRoles: ["RTM_USER", "RTM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
        },
      ]
    },
    {
      label: 'Settings',
      icon: 'settings',
      path: '',
      allowedRoles: ["RTM_USER", "RTM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
      subMenu: [
        {
          label: "Users",
          icon: 'contact',
          path: '/users',
          allowedRoles: ["RTM_MASTER", "CPM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
        }
      ]
    }
    // {
    //   label: "Change Point Management",
    //   icon: 'reload',
    //   path: '',
    //   subMenu: [
    //     {
    //       label: "Register",
    //       icon: 'document_plus', 
    //       path: "/change-point-management/create",
    //       allowedRoles: ["CPM_USER", "CPM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
    //     },
    //     {
    //       label: "Status/Update",
    //       icon: 'search', 
    //       path: "/change-point-management/search",
    //       allowedRoles: ["CPM_USER", "CPM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
    //     },
    //     {
    //       label: "Setup",
    //       icon: 'tool', 
    //       path: "/change-point-management/setup",
    //       allowedRoles: ["CPM_MASTER", "RTM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
    //     },
    //   ],
    //   allowedRoles: ["CPM_USER", "CPM_MASTER", "BRSA_ASSIST_SCANIA_IT"],
    // },
  ]

  @Output() eventFromSidebar = new EventEmitter<any>();

  selectedMenu$ = this.menuStateService.selectedMenu$;

  ngOnInit(): void {
    this.menuStateService.signal$.subscribe((value) => {
      this.mobileMenuAction(value);
    });

    this.appReadyService.appReady$
    .pipe(
      first()
    )
    .subscribe(() => {
      this.getUser();
    });
  }

  closeMobileMenu() {
    this.eventFromSidebar.emit({
      type: "showMobileMenu",
      value: false,
    });
  }

  closeMenu(menu: any) {
    if (!menu.subMenu) {
      this.eventFromSidebar.emit({
        type: "showMobileMenu",
        value: false,
      });
    }
  }

  closeSidebar(): void {
    this.collapsed = false;
  }

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
  }

  toggleParentClass(event: any, className: string) {
    const hasClass = event.target.parentElement.classList.contains(className);

    if (hasClass) {
      this.renderer.removeClass(event.target.parentElement, className);
    } else {
      this.renderer.addClass(event.target.parentElement, className);
    }
  }

  toggleClass(event: any, className: string) {
    const hasClass = event.target.classList.contains(className);

    if (hasClass) {
      this.renderer.removeClass(event.target, className);
    } else {
      this.renderer.addClass(event.target, className);
    }
  }

  canAccess(menuItem: any): boolean {
    return menuItem.allowedRoles?.some((role: string) => this.authService.user?.roles?.includes(role));
  }

  mobileMenuAction(action: boolean) {
    this.isMenuOpened = action;
  }

  navigate(menuKey: string, path: string) {
    this.isMenuOpened = false;
    this.menuStateService.setSelectedMenu(menuKey);
    this.router.navigateByUrl(path);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isMobile = event.target.innerWidth < 992;
  }

  public logout() {
    this.authService.logout();
  }

  public onChangeMarket(selected: number) {
    const market = this.userMarkets.find((c) => c.id === selected);
    if (market) {
      this.mktSelectService.updateSelectedMarket(market);
      this.selectedMarket = market;
      this.selectedMarketLabel = market.name;
      localStorage.setItem("rtm-cpm-market", JSON.stringify(market));   
    }
  }

  private getUser() {
    var userJson = sessionStorage.getItem("rtm-cpm-user");
    var user = JSON.parse(userJson!);

    this.userName = user.name;
    this.userEmail = user.email;
    this.userRoles = user.roles;
    this.userMarketRoles = user.markets ?? [];

    this.loadMarkets();
  }

  private loadMarkets(): void {
    this.getMarkets().subscribe({
      next: (value: Market[]) => {
        if (value && value.length > 0) {
          const markets = value.map((market) => {
            market.acronym = market.acronym.replace("#", "_");
            return market;
          });
          this.userMarkets = this.filterMarketsByUser(markets);
          this.restoreStateMarketsSelectAndClock();
        }
      },
      error: (err) => {
        //this.toast.showToast(TOAST_STATE.error, "Can't load markets");
      },
    });
  }

  private restoreStateMarketsSelectAndClock(){
    const savedMarket = localStorage.getItem("rtm-cpm-market");
    if (savedMarket) {
      const marketObj = JSON.parse(savedMarket);
      const restored = this.userMarkets.find(m => m.id === marketObj.id);
      if (restored) {
        this.selectedMarket = restored;
        this.selectedMarketLabel = restored.name;
        this.mktSelectService.updateSelectedMarket(restored);
        return;
      }
    }

    if (this.userMarkets.length > 0) {
      this.onChangeMarket(this.userMarkets[0].id);
    }
  }

  private getMarkets(): Observable<Market[]> {
    return this.userRoles.includes('BRSA_ASSIST_SCANIA_IT') ? this.marketService.getAll() : this.marketService.getAllEnabled();
  }

  private filterMarketsByUser(markets: Market[]): Market[] {
    if (this.userRoles.includes('BRSA_ASSIST_SCANIA_IT')) {
      return markets;
    }

    const allowedMarketRoles = new Set(
      this.userMarketRoles.map((marketRole) => this.normalizeMarketRole(marketRole))
    );

    return markets.filter((market) =>
      allowedMarketRoles.has(this.normalizeMarketRole('MARKET_' + market.acronym))
    );
  }

  private normalizeMarketRole(value: string): string {
    return value.trim().toUpperCase().replace('#', '_');
  }
}
